/**
 * Susurro monetization framework — local flags, NO ads.
 *
 * Product decision: there is NEVER advertising (incompatible with a private
 * messenger: ads require tracking). The model is freemium by CAPABILITIES:
 *   - `free`: 1:1 and normal groups, standard media. Forever.
 *   - `plus`: large groups, heavy media and E2E backup.
 *
 * The current plan is a LOCAL flag (`localStorage["susurro:plan"]`): when
 * a payment gateway exists (manual: owner's credentials/tax data), its
 * webhook/license will only have to write this same signed flag; all the
 * app's gates already consult this module (single choke point). The gates are
 * product, not security: everything remains E2E and zero-knowledge.
 */

export type PlanId = "free" | "plus";

interface Entitlements {
  /** Maximum size of a group (members, including you). */
  readonly maxGroupMembers: number;
  /** Maximum size of an E2E attachment (bytes). */
  readonly maxAttachmentBytes: number;
  /** E2E backup encrypted by passphrase (export). Restoration is ALWAYS free. */
  readonly backupE2E: boolean;
  /** Product constant: there are never ads, in any plan. */
  readonly ads: false;
}

const MIB = 1024 * 1024;

const PLAN_ENTITLEMENTS: Record<PlanId, Entitlements> = {
  free: { maxGroupMembers: 8, maxAttachmentBytes: 25 * MIB, backupE2E: false, ads: false },
  plus: { maxGroupMembers: 128, maxAttachmentBytes: 512 * MIB, backupE2E: true, ads: false },
};

const PLAN_KEY = "susurro:plan";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

function defaultStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** Current plan. Any missing/unknown value degrades to `free`. */
export function getPlan(storage: StorageLike | undefined = defaultStorage()): PlanId {
  try {
    const raw = storage?.getItem(PLAN_KEY);
    return raw === "plus" ? "plus" : "free";
  } catch {
    return "free";
  }
}

export function setPlan(plan: PlanId, storage: StorageLike | undefined = defaultStorage()): void {
  try {
    storage?.setItem(PLAN_KEY, plan);
  } catch {
    /* storage unavailable: we stay in memory as free */
  }
}

export function entitlementsFor(plan: PlanId): Entitlements {
  return PLAN_ENTITLEMENTS[plan];
}

/** Can a group of `memberCount` members (including you) be created with this plan? */
export function canCreateGroupOf(memberCount: number, plan: PlanId): boolean {
  return memberCount <= entitlementsFor(plan).maxGroupMembers;
}

/** Can a binary of `bytes` be attached with this plan? */
export function canSendAttachmentOf(bytes: number, plan: PlanId): boolean {
  return bytes <= entitlementsFor(plan).maxAttachmentBytes;
}

/** Can this plan EXPORT an E2E backup? (importing is always allowed). */
export function canUseBackup(plan: PlanId): boolean {
  return entitlementsFor(plan).backupE2E;
}
